import { useQuery } from "@tanstack/react-query";
import { ENDPOINTS } from "@/api/application-api";
import { useAuth } from "@/context/auth-context";
import { handleResponse } from "@/utils/errorResponseHandlers";
import { jsonAuthHeaders } from "@/utils/headers";
import { DateRange } from "react-day-picker";
import { addDays, formatISO } from "date-fns";

export type UsageMetricData = {
  used: number;
  included: number;
  excess: number;
  cost: number;
  data: number[];
  labels: string[];
};

type Params = {
  operationId: number;
  date: DateRange | undefined;
  organizationId?: number;
  workspaceIds?: string[];
};

const useUsageMetric = (params: Params) => {
  const userToken = useAuth()?.userToken!;
  const endpoint = ENDPOINTS.usages;

  const buildUrlWithParams = () => {
    const url = new URL(endpoint);

    const appendParam = (key: string, value: string | undefined) => {
      if (value) url.searchParams.append(key, value);
    };

    appendParam("operation_id", params.operationId?.toString());
    appendParam(
      "start_date",
      params.date?.from ? formatISO(params.date.from) : undefined,
    );
    appendParam(
      "end_date",
      params.date?.to ? formatISO(addDays(params.date.to, 1)) : undefined,
    );
    appendParam("organization_id", params.organizationId?.toString());

    if (params.workspaceIds?.length) {
      params.workspaceIds.forEach((id) => appendParam("workspace_ids", id));
    }

    return url.toString();
  };

  const fetchUsageMetric = async () => {
    const url = buildUrlWithParams();
    const response = await fetch(url, {
      method: "GET",
      headers: jsonAuthHeaders(userToken),
    });
    return handleResponse(response);
  };

  const isEnabled = !!params.date?.from && !!params.date?.to;

  return useQuery<UsageMetricData>([endpoint, params], fetchUsageMetric, {
    enabled: isEnabled,
  });
};

export { useUsageMetric };
